import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

// Compact play/pause control for a Cloudinary-hosted MP3.
// Shows play state + duration + a thin progress bar. Designed to drop
// into the TransmissionCard header and the Verdict card without
// dominating the layout.
//
// Exposes the underlying <audio> element via ref so parents can drive
// playback (e.g. the briefing page taps play() from a button outside
// the player). `onStart` fires once on first play — used to gate
// reveal of body text after the audio begins.

const VoicePlayer = forwardRef(function VoicePlayer(
  { url, duration, label = 'transmission audio', onStart },
  ref
) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  useImperativeHandle(ref, () => audioRef.current, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onPlay = () => {
      setPlaying(true);
      if (!startedRef.current) {
        startedRef.current = true;
        onStart?.();
      }
    };
    const onPauseOrEnd = () => setPlaying(false);
    const onTime = () => setProgress(a.currentTime / (a.duration || duration || 1));
    const onError = () => setError('audio failed to load');
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPauseOrEnd);
    a.addEventListener('ended', onPauseOrEnd);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('error', onError);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPauseOrEnd);
      a.removeEventListener('ended', onPauseOrEnd);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('error', onError);
    };
  }, [duration, onStart]);

  if (!url) return null;

  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch((err) => setError(err.message));
    } else {
      a.pause();
    }
  }

  return (
    <div className="flex items-center gap-3">
      <audio ref={audioRef} src={url} preload="metadata" aria-label={label} />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        aria-pressed={playing}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-action bg-action text-action-fg transition hover:bg-action-hover"
      >
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M7 4l13 8-13 8V4z" />
          </svg>
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          <span>{label}</span>
          {duration ? <span className="tabular-nums">{formatDuration(duration)}</span> : null}
        </div>
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-bg-tertiary">
          <div
            className="h-full bg-action transition-[width]"
            style={{ width: `${Math.min(100, Math.max(0, progress * 100)).toFixed(1)}%` }}
          />
        </div>
        {error && <p className="mt-1 font-mono text-[10px] text-danger">{error}</p>}
      </div>
    </div>
  );
});

export default VoicePlayer;

function formatDuration(s) {
  if (!Number.isFinite(s)) return '';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
